import _isEqual from "lodash/isEqual";
import times from "lodash/times";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import React, { useRef, useCallback, useState } from "react";
import { Grid, Menu, MenuItem, Typography, RootRef } from "@material-ui/core";

import {
  createCardMove,
  initializeCreateCard,
} from "features/planner/planner.actions";

import Row from "features/planner/components/Row";
import { invokeIfFunction } from "utils/app.util";
import DraggableCardTarget from "features/planner/components/DraggableCardTarget";
import { EMPTY_MAP } from "app/app.constants";
import { createSelector } from "reselect";
import { GROUP_ROW_COUNT } from "../planner.constants";

const useStyles = makeStyles(theme =>
  createStyles({
    headerContainer: {
      alignItems: "center",
      padding: `0 ${theme.spacing(1)}px`,
    },
    header: {
      fontWeight: 700,
    },
    rowPlaceholder: {
      display: "none",
      alignItems: "center",
      color: theme.palette.text.hint,
    },
    rowRoot: {
      cursor: "pointer",
      "&:hover": {
        background: theme.palette.grey[100],
      },
      "&:hover $rowPlaceholder": {
        display: "flex",
      },
    },
    cardContainer: {
      overflow: "hidden",
      "&:nth-of-type(even)": {
        borderLeft: `1px solid ${theme.altus.background.view}`,
      },
    },
  }),
);

const CardContainer = ({ groupId, cardContainerId }) => {
  const classes = useStyles();

  const rootRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElSlotNo, setAnchorElSlotNo] = useState(null);
  const dispatch = useDispatch();

  const bySlotNo = useCallback(selectBySlotNo, []);
  const cardsBySlotNo = useSelector(
    state => bySlotNo(state, cardContainerId),
    _isEqual,
  );

  const rowCount = useCallback(selectCardContainerRowCount, []);
  const cardContainerRowCount = useSelector(
    state => rowCount(state, groupId),
    _isEqual,
  );

  const cardContainer = useSelector(state =>
    state.getIn([
      "groups",
      groupId.toString(),
      "cardContainersById",
      cardContainerId.toString(),
    ]),
  );

  const createCard = useCallback(
    callback => {
      invokeIfFunction(callback);
      dispatch(initializeCreateCard(cardContainerId, anchorElSlotNo));
    },
    [anchorElSlotNo, cardContainerId, dispatch],
  );

  const closeRowMenu = useCallback(() => setAnchorEl(null), []);

  return (
    <RootRef rootRef={rootRef} key={`group-${groupId}-${cardContainerId}`}>
      <Grid container className={classes.cardContainer}>
        {cardContainer.get("heading") && (
          <Grid item xs={12}>
            <Row>
              <Grid container className={classes.headerContainer}>
                <Typography variant="body2" className={classes.header}>
                  {cardContainer.get("heading")}
                </Typography>
              </Grid>
            </Row>
          </Grid>
        )}
        {times(cardContainerRowCount, slotNo => {
          const card = cardsBySlotNo?.get(slotNo?.toString());

          return (
            <DraggableCardTarget
              key={slotNo}
              slotNo={slotNo}
              groupId={groupId}
              onCardMove={v => dispatch(createCardMove(v))}
              cardContainerId={cardContainer.get("id")}
              cardId={card ? card.get("id") : undefined}
              cardContainerType={cardContainer.get("type")}
              cardType={card ? card.get("type") : undefined}
              setAnchorEl={setAnchorEl}
              setAnchorElSlotNo={setAnchorElSlotNo}
            />
          );
        })}
        <Menu
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={closeRowMenu}
          PaperProps={{
            style: {
              width: rootRef.current ? rootRef.current.clientWidth : "auto",
            },
          }}
        >
          <MenuItem onClick={() => createCard(closeRowMenu)}>New card</MenuItem>
        </Menu>
      </Grid>
    </RootRef>
  );
};

// Selectors Used by CardContainer - performance critical selectors to cache cards
const selectCards = state => state.get("cards");

const selectPlanningCards = createSelector([selectCards], cards => {
  return cards.filter(card => !card.getIn(["employee", "hideInPlanning"]));
});

const selectBySlotNo = createSelector(
  [selectPlanningCards, (cards, cardContainerId) => cardContainerId],
  (cards, cardContainerId) => {
    return cards
      .filter(
        card => card.get("cardContainerId")?.toString() === cardContainerId,
      )
      .reduce(
        (result, card) => result.setIn([card.get("slotNo").toString()], card),
        EMPTY_MAP,
      );
  },
);

const selectCardContainerRowCount = createSelector(
  [selectPlanningCards, (cards, groupId) => groupId],
  (cards, groupId) => {
    const r = cards
      .groupBy(card => card.get("groupId").toString())
      .get(groupId.toString());

    const maxUsedSlotNumberCard = r
      ? r.maxBy(card => card.get("slotNo"))
      : undefined;

    return maxUsedSlotNumberCard
      ? Math.max(
          GROUP_ROW_COUNT.MINIMUM,
          maxUsedSlotNumberCard.get("slotNo") + GROUP_ROW_COUNT.EMPTY_ROWS + 1,
        )
      : GROUP_ROW_COUNT.MINIMUM;
  },
);

export default CardContainer;
